import React, { useEffect, useCallback, useState, useRef } from "react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;

function ImageModal({ images, currentIndex, onClose, onPrev, onNext }) {
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null); // { startX, startY, startOffX, startOffY }
    const imgRef = useRef(null);

    // Reset zoom/pan when image changes
    useEffect(() => {
        setZoom(1);
        setOffset({ x: 0, y: 0 });
    }, [currentIndex]);

    const clampOffset = useCallback((x, y, currentZoom, el) => {
        if (!el || currentZoom <= 1) return { x: 0, y: 0 };
        const rect = el.getBoundingClientRect();
        const maxX = (rect.width * (currentZoom - 1)) / 2;
        const maxY = (rect.height * (currentZoom - 1)) / 2;
        return {
            x: Math.max(-maxX, Math.min(maxX, x)),
            y: Math.max(-maxY, Math.min(maxY, y)),
        };
    }, []);

    const changeZoom = useCallback((delta) => {
        setZoom(prev => {
            const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta));
            if (next <= 1) setOffset({ x: 0, y: 0 });
            return next;
        });
    }, []);

    const handleKey = useCallback((e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "ArrowLeft" && onPrev) onPrev();
        if (e.key === "ArrowRight" && onNext) onNext();
        if (e.key === "+" || e.key === "=") changeZoom(ZOOM_STEP);
        if (e.key === "-") changeZoom(-ZOOM_STEP);
        if (e.key === "0") { setZoom(1); setOffset({ x: 0, y: 0 }); }
    }, [onClose, onPrev, onNext, changeZoom]);

    useEffect(() => {
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [handleKey]);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
        setZoom(prev => {
            const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta));
            if (next <= 1) setOffset({ x: 0, y: 0 });
            return next;
        });
    }, []);

    const handleImgClick = useCallback(() => {
        setZoom(prev => {
            if (prev < 2) {
                return 2;
            }
            setOffset({ x: 0, y: 0 });
            return 1;
        });
    }, []);

    const handleMouseDown = useCallback((e) => {
        if (zoom <= 1) return;
        e.preventDefault();
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startOffX: offset.x,
            startOffY: offset.y,
        };
    }, [zoom, offset]);

    const handleMouseMove = useCallback((e) => {
        if (!dragRef.current) return;
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        const clamped = clampOffset(
            dragRef.current.startOffX + dx,
            dragRef.current.startOffY + dy,
            zoom,
            imgRef.current
        );
        setOffset(clamped);
    }, [zoom, clampOffset]);

    const handleMouseUp = useCallback(() => {
        dragRef.current = null;
    }, []);

    if (!images || images.length === 0) return null;
    const img = images[currentIndex];
    if (!img) return null;

    const isZoomed = zoom > 1;

    return (
        <div
            className="image-modal-overlay"
            onClick={onClose}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
        >
            <div
                className="image-modal-content"
                onClick={(e) => e.stopPropagation()}
                onWheel={handleWheel}
                style={{ position: "relative", overflow: "hidden" }}
            >
                <button className="image-modal-close" onClick={onClose}>X</button>
                {images.length > 1 && onPrev && (
                    <button className="image-modal-nav image-modal-prev" onClick={onPrev}>&#8249;</button>
                )}

                {/* Zoom controls */}
                <div className="image-modal-zoom-controls" onClick={(e) => e.stopPropagation()}>
                    <button
                        className="image-modal-zoom-btn"
                        onClick={() => changeZoom(-ZOOM_STEP)}
                        disabled={zoom <= MIN_ZOOM}
                        title="Zoom out (−)"
                    >−</button>
                    <span className="image-modal-zoom-label">{Math.round(zoom * 100)}%</span>
                    <button
                        className="image-modal-zoom-btn"
                        onClick={() => changeZoom(ZOOM_STEP)}
                        disabled={zoom >= MAX_ZOOM}
                        title="Zoom in (+)"
                    >+</button>
                    {isZoomed && (
                        <button
                            className="image-modal-zoom-btn"
                            onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}
                            title="Reset zoom (0)"
                        >Reset</button>
                    )}
                </div>

                <img
                    ref={imgRef}
                    src={img.url || img.imageUrl || img}
                    alt={img.filename || "Capture"}
                    className="image-modal-img"
                    style={{
                        transform: `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`,
                        transformOrigin: "center center",
                        cursor: isZoomed ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
                        transition: dragRef.current ? "none" : "transform 0.15s ease",
                        userSelect: "none",
                    }}
                    onClick={handleImgClick}
                    onMouseDown={handleMouseDown}
                    draggable={false}
                />

                {images.length > 1 && onNext && (
                    <button className="image-modal-nav image-modal-next" onClick={onNext}>&#8250;</button>
                )}
                <div className="image-modal-info">
                    {img.zone && <span>{img.zone}</span>}
                    {img.capturedAt && <span>{new Date(img.capturedAt).toLocaleString()}</span>}
                    {img.filename && <span>{img.filename}</span>}
                    <span>{currentIndex + 1} / {images.length}</span>
                </div>
            </div>
        </div>
    );
}

export default ImageModal;
